import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Send, Smartphone, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import {
  isPushSupported,
  isPushEnabled,
  isStandalonePWA,
  subscribePush,
  unsubscribePush,
  hasBeenPrompted,
  markPromptShown,
} from '../lib/pushNotifications';

/**
 * Inline toggle the user can flip on/off from their profile/settings.
 * Renders nothing if push isn't supported on the device.
 */
export function NotificationToggle({ className = '' }) {
  const [enabled, setEnabled] = useState(false);
  const [supported, setSupported] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) return;
      setSupported(true);
      const on = await isPushEnabled();
      if (!cancelled) setEnabled(on);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!supported) return null;

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (enabled) {
        const ok = await unsubscribePush();
        if (ok) {
          setEnabled(false);
          toast.success('Notifications turned off');
        }
      } else {
        if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
          toast.error("Notifications are blocked in your browser settings. Enable them and try again.");
          return;
        }
        const ok = await subscribePush();
        if (ok) {
          setEnabled(true);
          toast.success("You're all set — we'll let you know about new bids & updates");
        } else {
          toast.error("Couldn't enable notifications. Make sure you allowed them in the browser prompt.");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={handleToggle}
        disabled={busy}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${
          enabled
            ? 'bg-[#2E7D32] text-white border-[#1B5E20] hover:bg-[#1B5E20]'
            : 'bg-white text-[#050505] border-gray-300 hover:border-[#2E7D32]'
        } disabled:opacity-60 ${className}`}
        data-testid="notification-toggle"
        aria-pressed={enabled}
      >
        {enabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
        {enabled ? 'Notifications on' : 'Turn on notifications'}
      </button>
      {enabled && (
        <button
          type="button"
          onClick={async () => {
            try {
              const { data } = await api.post('/push/test');
              if (data?.sent > 0) {
                toast.success('Test sent — check your notification tray');
              } else {
                toast.error("No active subscription on this device. Try turning notifications off and on again.");
              }
            } catch {
              toast.error('Test push failed — please try again');
            }
          }}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold text-[#1B5E20] bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
          data-testid="notification-test-btn"
          title="Send a test notification to this device"
        >
          <Send className="w-3.5 h-3.5" /> Send test
        </button>
      )}
    </div>
  );
}

/**
 * One-shot inline banner shown once per user. Asks for permission with a
 * friendly explanation. Dismissed permanently via localStorage.
 */
export function NotificationPrompt({ role }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) return;
      // Skip if already granted+subscribed — they're all set.
      if (await isPushEnabled()) return;
      // If the user previously dismissed the banner we still want to nudge
      // them once more if push is not actually enabled (permission can be
      // granted with no subscription, e.g. from old code paths). Only stop
      // pestering when the user has explicitly DENIED notifications.
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return;
      if (hasBeenPrompted() && Notification.permission === 'granted') {
        // Old auto-grant path — give them ONE more chance to actually subscribe
        // by clearing the flag so the banner reappears, then bail this cycle.
        // (They'll see it on next mount and can decide.)
        try { localStorage.removeItem('jd_push_prompted_at'); } catch { /* ignore */ }
      } else if (hasBeenPrompted()) {
        return;
      }
      // iOS-only PWA caveat — skip the banner if we're in iOS browser
      // (push won't work until they install the PWA; the install banner
      // handles that flow separately)
      const ua = window.navigator.userAgent;
      const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
      if (isIos && !isStandalonePWA()) return;
      if (!cancelled) setShow(true);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!show) return null;

  const handleEnable = async () => {
    setBusy(true);
    try {
      const ok = await subscribePush();
      markPromptShown();
      setShow(false);
      if (ok) {
        toast.success("Great — we'll alert you about new bids & updates");
      } else if (Notification.permission === 'denied') {
        toast.error("Notifications are blocked in browser settings. You can enable them later from your profile.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    markPromptShown();
    setShow(false);
  };

  const copy =
    role === 'driver'
      ? "Get instant alerts when new jobs hit your area and when your bid is accepted."
      : role === 'admin'
        ? "Get instant alerts when a new customer or driver signs up, when deliveries are posted, and when support messages come in."
        : "Get instant alerts when drivers bid on your delivery and when your driver is on the way.";

  return (
    <div
      className="mb-6 rounded-xl bg-emerald-50/80 border border-emerald-100 px-4 py-3 sm:px-5 sm:py-4 flex items-start gap-3 sm:gap-4 shadow-sm"
      data-testid="notification-prompt-banner"
      role="region"
      aria-label="Enable notifications"
    >
      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white border border-emerald-100 flex items-center justify-center relative">
        <Smartphone className="w-5 h-5 sm:w-6 sm:h-6 text-[#1B5E20]" strokeWidth={1.75} />
        <Bell className="absolute -bottom-1 -right-1 w-4 h-4 text-[#F57E0E] bg-white rounded-full p-0.5 ring-1 ring-emerald-100" strokeWidth={2.4} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm sm:text-base font-bold text-[#050505] leading-tight">
          Turn on notifications
        </p>
        <p className="text-xs sm:text-sm text-gray-600 mt-0.5 leading-snug">{copy}</p>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-xs sm:text-sm font-bold transition-colors active:scale-95 disabled:opacity-60"
            data-testid="notification-prompt-enable"
          >
            <Bell className="w-3.5 h-3.5" /> Enable
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="px-3.5 py-2 text-xs sm:text-sm font-semibold text-gray-500 hover:text-gray-700"
            data-testid="notification-prompt-skip"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="flex-shrink-0 -mt-1 -mr-1 p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
        data-testid="notification-prompt-dismiss"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default NotificationPrompt;
